import type { BlogArticle } from '@/lib/blog-data'
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count'
import { TESLA_FACTS } from '@/lib/tesla-facts'

export const post: BlogArticle = {
    slug: 'tesla-lock-sound-not-available-region-fix',
    title: 'Tesla Lock Sound Option Missing? What to Check',
    excerpt: 'No Lock Sound option under Toybox → Boombox? It comes down to the external speaker and your software version. Here is how to check both.',
  image: '/images/blog/generated/tesla-lock-sound-not-available-region-fix.png',
    content: `
# Tesla Lock Sound Option Missing? What to Check

You found a lock sound, opened Toybox, and there is no Lock Sound option. Two things decide whether it shows up: whether your car has the external speaker, and whether your software is new enough.

${TESLA_FACTS.compatibility.statement}

## Check 1: Software

Custom lock sounds arrived with ${TESLA_FACTS.firmware.minimumLabel}. Open **Controls → Software** and install any pending update, then look again under **${TESLA_FACTS.menu.path}**.

## Check 2: The external speaker

The custom lock sound plays through the external pedestrian-warning speaker. Cars without it do not show the option:

- Model 3 built before September 2019
- Model S and Model X from before the 2021 refresh (not confirmed either way; check the menu)

Tesla's documentation does not describe a way to play a USB lock sound through the cabin speakers on a car without the external speaker. If the option is missing after a software update, your car most likely does not have it. Tesla Service can confirm what your VIN was built with.

## Check 3: Boombox while driving (US)

In the US, NHTSA recalls [22V-063](https://www.nhtsa.gov/recalls?nhtsaId=22V063) and [22V-235](https://www.nhtsa.gov/recalls?nhtsaId=22V235) (2022) led Tesla to disable Boombox when the car is in Drive, Neutral or Reverse, including Summon and Smart Summon. The lock sound plays when you lock the car, so it is a different use. If you cannot find the Boombox menu at all, work through checks 1 and 2 first. If your lock sound disappeared after an update, see [lock sound resets after update](/blog/tesla-lock-sound-resets-after-update).

## If the option is there

1. Format a USB drive as FAT32 or exFAT ([USB format guide](/blog/tesla-usb-format-lock-sound-fat32-exfat)).
2. Put \`${TESLA_FACTS.usb.filename}\` in a folder named \`${TESLA_FACTS.usb.folder}\`, so the path is \`${TESLA_FACTS.usb.path}\`.
3. Plug the drive into ${TESLA_FACTS.usb.portNote}.
4. Open ${TESLA_FACTS.menu.finalStep}.

${TESLA_FACTS.usb.rootFallback}

## FAQ

### Can I use custom lock sounds on a UK or EU Tesla?
Only if the car has the external speaker. Check whether **${TESLA_FACTS.menu.path}** exists on your touchscreen. If it does, the install is the same as everywhere else.

### Why does my friend have the option and I do not?
Either their car has the external speaker and yours does not, or their software is newer. Update first, then check the menu again.

### Does the lock sound play when I unlock?
No. Your custom \`LockChime.wav\` plays on lock only, and Tesla does not document a separate custom unlock sound.

---

If the sound plays but is too quiet, see the [volume fix guide](/blog/fix-quiet-tesla-lock-sound-2025-45).

Browse [${PUBLIC_SOUND_COUNT_LABEL} free Tesla lock sounds](/sounds) ready to download as \`LockChime.wav\`, or use the [free converter](/convert) for your own audio. For the full install, see the [complete guide](/guide).
    `,
    author: 'Caleb Leigh',
    publishedAt: '2026-04-14',
    category: 'tips-tricks',
    tags: ['region', 'country', 'UK', 'EU', 'Europe', 'Boombox', 'not-available', 'PWS', 'recall'],
    readingTime: 4,
    soundCategory: 'trending',
    soundLimit: 4,
    featured: false,
  }
